import { type RefObject, useEffect, useState } from 'react';

export interface ScrollEdges {
  /** There is more row to reach to the left. */
  start: boolean;
  /** There is more row to reach to the right. */
  end: boolean;
}

/**
 * Whether a horizontally scrolling row genuinely has more to give in either direction, read from
 * its own scroll geometry rather than a breakpoint. A breakpoint assumes which widths overflow;
 * the measurement is right at every width, in a narrow card as much as on a phone.
 *
 * Same measure `ComposerModePills` uses for its caret badges. Pass `enabled: false` to skip the
 * listeners entirely.
 */
export function useScrollEdges(
  ref: RefObject<HTMLElement | null>,
  enabled: boolean = true,
  /** Re-measure when the content changes (the options in a toggle, say). */
  contentKey?: unknown,
): ScrollEdges {
  const [edges, setEdges] = useState<ScrollEdges>({ start: false, end: false });

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) {
      setEdges((previous) =>
        previous.start || previous.end ? { start: false, end: false } : previous,
      );
      return;
    }
    const update = () => {
      const next = {
        start: el.scrollLeft > 1,
        end: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
      };
      setEdges((previous) =>
        previous.start === next.start && previous.end === next.end ? previous : next,
      );
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, [ref, enabled, contentKey]);

  return edges;
}
